import React, { useMemo } from 'react';
import { Card, List, Typography, Spin, Alert } from 'antd';
import { ShopOutlined } from '@ant-design/icons';
import { Tenant as ApiTenant } from '@/api/services/tenants.service';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/features/auth/LanguageSwitcher';
import { ThemeToggle } from '@/components/features/auth/ThemeToggle';
import { useListAllTenants } from '@/hooks/useTenants';
import './TenantSelectorPage.css';

const { Title, Paragraph } = Typography;

// Tenants to ignore from the list (e.g., test tenants)
const IGNORED_TENANT_CODES = ['TESTE2E'];

interface TenantDisplay {
  id: string;
  name: string;
  code: string;
  url: string;
  description: string;
}

export const TenantSelectorPage: React.FC = () => {
  const { t } = useTranslation();

  // Use React Query hook - prevents multiple API calls and provides caching
  const { data: apiTenants, isLoading, isError } = useListAllTenants();

  // Transform API data to display format using useMemo
  const tenants = useMemo(() => {
    if (!apiTenants) return [];

    // Get current port (only if non-standard)
    const currentPort = window.location.port;
    const protocol = window.location.protocol;
    // Only add port if it's specified (not standard 80/443)
    const portSuffix = currentPort ? `:${currentPort}` : '';

    // Filter out ignored tenants and map to display format
    return apiTenants
      .filter(
        (tenant: ApiTenant) => !IGNORED_TENANT_CODES.includes(tenant.code)
      )
      .map((tenant: ApiTenant) => {
        // Use display_name if available, otherwise fall back to code
        const displayName = tenant.display_name || tenant.code;

        return {
          id: tenant.__meta__.id,
          name: displayName,
          code: tenant.code,
          url: `${protocol}//${tenant.code.toLowerCase()}.thilo.loc${portSuffix}`,
          description: `${displayName} tenant aplikacija`,
        };
      });
  }, [apiTenants]);

  return (
    <div className="tenant-selector-container">
      <div className="tenant-selector-content">
        {/* Top controls */}
        <div className="tenant-selector-top-controls">
          <LanguageSwitcher />
          <div style={{ marginLeft: 12 }}>
            <ThemeToggle />
          </div>
        </div>

        {/* Logo */}
        <div className="tenant-selector-logo-wrapper">
          <img
            src="/v2/api/tenants/code/THILO/logo.png"
            alt="Thilo logo"
            className="tenant-selector-logo-img"
            onError={(e) => {
              // Fallback to text if logo fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'block';
            }}
          />
          <div className="tenant-selector-logo" style={{ display: 'none' }}>
            Thilo
          </div>
        </div>

        {/* Main Card */}
        <Card className="tenant-selector-card">
          <Title level={2} className="tenant-selector-title">
            {t('tenantSelector.title')}
          </Title>

          <Paragraph className="tenant-selector-description">
            {t('tenantSelector.description')}
          </Paragraph>

          {isLoading && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16, color: '#6b7280' }}>
                {t('tenantSelector.loading')}
              </p>
            </div>
          )}

          {isError && (
            <Alert
              message={t('tenantSelector.error')}
              description={t('tenantSelector.errorLoading')}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {!isLoading && !isError && tenants.length === 0 && (
            <Alert
              message={t('tenantSelector.noTenants')}
              description={t('tenantSelector.noTenantsDescription')}
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {!isLoading && !isError && tenants.length > 0 && (
            <List
              className="tenant-list"
              dataSource={tenants}
              renderItem={(tenant) => (
                <List.Item className="tenant-list-item">
                  <a href={tenant.url} className="tenant-link" target="_self">
                    <div className="tenant-link-content">
                      <div className="tenant-icon">
                        <img
                          src={`/v2/api/tenants/code/${tenant.code.toUpperCase()}/logo.png`}
                          alt={`${tenant.name} logo`}
                          className="tenant-logo-img"
                          onError={(e) => {
                            // Fallback to icon if logo fails to load
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback =
                              target.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div
                          className="tenant-logo-fallback"
                          style={{ display: 'none' }}
                        >
                          <ShopOutlined />
                        </div>
                      </div>
                      <div className="tenant-info">
                        <div className="tenant-name">{tenant.name}</div>
                        <div className="tenant-description">
                          {tenant.description}
                        </div>
                        <div className="tenant-url">{tenant.url}</div>
                      </div>
                    </div>
                  </a>
                </List.Item>
              )}
            />
          )}
        </Card>

        {/* Footer */}
        <div className="tenant-selector-footer">Powered by: ⚡ Thilo</div>
      </div>
    </div>
  );
};
